import { useState, useRef } from "react";
import { toast } from "sonner";
import { BookMarked, Plus, Trash2, UserCircle2, Loader2, UploadCloud, Lock, Unlock, Pencil, X, Merge } from "lucide-react";
import { MergeCharactersDialog } from "@/components/MergeCharactersDialog";
import api, { mediaUrl } from "@/lib/api";
import CharacterVoice from "@/components/CharacterVoice";

const EMPTY = { name: "", role: "", age: "", wardrobe: "", description: "" };
const normName = (n) => (n || "").trim().toLowerCase().replace(/\s+/g, " ");

function PhotoSlot({ label, testId, preview, existing, onPick }) {
  const ref = useRef(null);
  const src = preview || (existing ? mediaUrl(existing) : null);
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div onClick={() => ref.current?.click()}
        className="relative w-24 h-24 rounded-xl border-2 border-dashed border-white/15 hover:border-[#FF334B] cursor-pointer overflow-hidden flex items-center justify-center"
        data-testid={testId}>
        {src ? <img src={src} alt={label} className="w-full h-full object-cover" />
          : <div className="text-center text-[#64748B]"><UploadCloud className="w-5 h-5 mx-auto mb-1" /></div>}
        <input ref={ref} type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); }} className="hidden" />
      </div>
      <span className="text-[11px] text-[#64748B]">{label}</span>
    </div>
  );
}

export default function CastPanel({ characters, reload }) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState({ face: null, profile: null, body: null });
  const [previews, setPreviews] = useState({ face: null, profile: null, body: null });
  const [saving, setSaving] = useState(false);

  const editing = editingId ? characters.find((c) => c.id === editingId) : null;
  const nameCount = characters.reduce((m, c) => ({ ...m, [normName(c.name)]: (m[normName(c.name)] || 0) + 1 }), {});
  const dupList = [...new Set(characters.filter((c) => nameCount[normName(c.name)] > 1).map((c) => c.name.trim()))];
  const [mergePair, setMergePair] = useState(null);
  const dupPair = (nm) => characters.filter((c) => normName(c.name) === normName(nm)).slice(0, 2);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pickFile = (slot, f) => {
    setFiles((s) => ({ ...s, [slot]: f }));
    setPreviews((s) => ({ ...s, [slot]: URL.createObjectURL(f) }));
  };

  const openNew = () => {
    setEditingId(null); setForm(EMPTY);
    setFiles({ face: null, profile: null, body: null });
    setPreviews({ face: null, profile: null, body: null });
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ name: c.name || "", role: c.role || "", age: c.age || "", wardrobe: c.wardrobe || "", description: c.description || "" });
    setFiles({ face: null, profile: null, body: null });
    setPreviews({ face: null, profile: null, body: null });
    setOpen(true);
  };
  const close = () => { setOpen(false); setEditingId(null); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Ponle un nombre al personaje"); return; }
    if (!editingId && !files.face) { toast.error("Sube la foto del rostro"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (files.face) fd.append("file", files.face);
      if (files.profile) fd.append("photo_profile", files.profile);
      if (files.body) fd.append("photo_body", files.body);
      const url = editingId ? `/characters/${editingId}` : "/characters";
      await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(editingId ? "Ficha actualizada" : "Personaje añadido a la biblia");
      close();
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    await api.delete(`/characters/${id}`);
    await reload();
    toast.success("Personaje eliminado");
  };

  const toggleLock = async (c) => {
    try {
      await api.post(`/characters/${c.id}/lock`, { locked: !c.locked });
      await reload();
      toast.success(!c.locked ? "🔒 Personaje bloqueado: se verá igual en cada escena" : "Personaje desbloqueado");
    } catch { toast.error("No se pudo cambiar el bloqueo"); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 fade-up">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BookMarked className="w-6 h-6 text-[#FF334B]" />
          <div>
            <h2 className="font-head text-2xl sm:text-3xl font-bold tracking-tight">Biblia de Continuidad</h2>
            <p className="text-xs text-[#64748B] mt-0.5">Crea fichas (rostro, voz, ropa, edad) y bloquéalas para que se vean iguales en cada escena.</p>
          </div>
        </div>
        <button data-testid="add-character-button" onClick={openNew}
          className="inline-flex items-center gap-2 bg-[#FF334B] hover:bg-[#E0223A] text-white font-semibold px-4 py-2.5 rounded-full transition-colors shrink-0">
          <Plus className="w-4 h-4" /> Personaje
        </button>
      </div>

      {open && (
        <div className="mfp-card p-6 space-y-5 fade-up" data-testid="character-form">
          <div className="flex items-center justify-between">
            <span className="font-head font-semibold text-lg">{editingId ? "Editar ficha" : "Nueva ficha de personaje"}</span>
            <button data-testid="close-character-form" onClick={close} className="text-[#64748B] hover:text-white"><X className="w-5 h-5" /></button>
          </div>

          <div>
            <label className="mono-label">Referencias visuales</label>
            <div className="mt-3 flex flex-wrap gap-4">
              <PhotoSlot label="Rostro" testId="character-photo-face" preview={previews.face} existing={editing?.photo_path} onPick={(f) => pickFile("face", f)} />
              <PhotoSlot label="Perfil" testId="character-photo-profile" preview={previews.profile} existing={editing?.photo_profile_path} onPick={(f) => pickFile("profile", f)} />
              <PhotoSlot label="Cuerpo" testId="character-photo-body" preview={previews.body} existing={editing?.photo_body_path} onPick={(f) => pickFile("body", f)} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="character-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Nombre del personaje"
              className="w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FF334B]" />
            <input data-testid="character-role-input" value={form.role} onChange={(e) => set("role", e.target.value)} placeholder="Rol (protagonista, antagonista…)"
              className="w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FF334B]" />
            <input data-testid="character-age-input" value={form.age} onChange={(e) => set("age", e.target.value)} placeholder="Edad visual (ej. 28 años)"
              className="w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FF334B]" />
            <input data-testid="character-wardrobe-input" value={form.wardrobe} onChange={(e) => set("wardrobe", e.target.value)} placeholder="Vestuario (ej. chaqueta de cuero negra)"
              className="w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FF334B]" />
          </div>
          <textarea data-testid="character-description-input" value={form.description} onChange={(e) => set("description", e.target.value)} rows={3}
            placeholder="Rasgos físicos: color de piel, peinado, ojos, rasgos distintivos, personalidad…"
            className="w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FF334B]" />

          <button data-testid="save-character-button" onClick={save} disabled={saving}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#FF334B] hover:bg-[#E0223A] text-white font-semibold py-3 rounded-full transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-5 h-5 spin" /> : <Plus className="w-5 h-5" />} {editingId ? "Guardar cambios" : "Guardar personaje"}
          </button>
        </div>
      )}

      {characters.length === 0 && !open ? (
        <div className="mfp-card border-dashed p-12 text-center">
          <UserCircle2 className="w-12 h-12 mx-auto text-[#334155] mb-3" />
          <p className="text-[#94A3B8]">Aún no tienes personajes en tu biblia.</p>
          <p className="text-sm text-[#64748B]">Crea una ficha con rostro, edad, vestuario y voz, y bloquéala para mantenerlo consistente en tus videos y cortometrajes.</p>
        </div>
      ) : (
        <>
        {dupList.length > 0 && (
          <div className="mfp-card border-[#FF9F2E]/40 p-4 text-sm text-[#FFB869] space-y-3" data-testid="cast-duplicates-banner">
            <p>Tienes personajes repetidos: <strong>{dupList.join(", ")}</strong>. Únelos en uno solo: tú eliges qué foto y qué voz conservar, y sus series y capítulos pasan al que queda.</p>
            <div className="flex flex-wrap gap-2">
              {dupList.map((nm) => (
                <button key={nm} onClick={() => setMergePair(dupPair(nm))} data-testid={`merge-dup-btn-${normName(nm).replace(/\s+/g, "-")}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-[#FF9F2E] text-black hover:opacity-90 transition-opacity">
                  <Merge className="w-3.5 h-3.5" /> Unir «{nm}»
                </button>
              ))}
            </div>
          </div>
        )}
        {characters.length >= 2 && dupList.length === 0 && (
          <button onClick={() => setMergePair(characters.slice(0, 2))} data-testid="merge-any-btn"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-white/15 text-[#94A3B8] hover:text-white transition-colors">
            <Merge className="w-3.5 h-3.5" /> Unir dos personajes
          </button>
        )}
        {mergePair && <MergeCharactersDialog characters={characters} pair={mergePair} onClose={() => setMergePair(null)} onMerged={reload} />}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {characters.map((c) => (
            <div key={c.id} className={`mfp-card overflow-hidden ${c.locked ? "ring-1 ring-[#FF9F2E]/40" : ""}`} data-testid={`character-card-${c.id}`}>
              <div className="flex gap-3 p-4">
                <div className="w-20 h-20 rounded-xl overflow-hidden bg-black shrink-0">
                  {c.photo_path
                    ? <img src={mediaUrl(c.photo_path)} alt={c.name} className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center"><UserCircle2 className="w-8 h-8 text-[#334155]" /></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-head font-semibold truncate">{c.name}</div>
                    {nameCount[normName(c.name)] > 1 && (
                      <span data-testid={`character-dup-badge-${c.id}`} className="text-[10px] font-semibold text-[#FF9F2E] bg-[#FF9F2E]/12 px-1.5 py-0.5 rounded-full">
                        duplicado{c.voice_path ? " · voz ✓" : " · sin voz"}
                      </span>
                    )}
                    {c.locked && (
                      <span data-testid={`character-locked-badge-${c.id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#FF9F2E] bg-[#FF9F2E]/12 px-1.5 py-0.5 rounded-full">
                        <Lock className="w-2.5 h-2.5" /> ADN
                      </span>
                    )}
                  </div>
                  {c.role && <div className="mono-label mt-0.5">{c.role}</div>}
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {c.age && <span className="text-[11px] text-[#94A3B8] bg-white/5 px-2 py-0.5 rounded-full">{c.age}</span>}
                    {c.wardrobe && <span className="text-[11px] text-[#94A3B8] bg-white/5 px-2 py-0.5 rounded-full truncate max-w-[160px]">{c.wardrobe}</span>}
                  </div>
                  {c.description && <p className="text-xs text-[#64748B] mt-1.5 line-clamp-2">{c.description}</p>}
                </div>
              </div>

              <div className="px-4 pb-4 space-y-3">
                {(c.photo_profile_path || c.photo_body_path) && (
                  <div className="flex gap-2">
                    {c.photo_profile_path && <img src={mediaUrl(c.photo_profile_path)} alt="perfil" className="w-12 h-12 rounded-lg object-cover" />}
                    {c.photo_body_path && <img src={mediaUrl(c.photo_body_path)} alt="cuerpo" className="w-12 h-12 rounded-lg object-cover" />}
                  </div>
                )}
                <CharacterVoice characterId={c.id} hasVoice={!!c.voice_path} onDone={reload} />
                <div className="flex items-center gap-2">
                  <button data-testid={`character-lock-${c.id}`} onClick={() => toggleLock(c)}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 text-sm font-semibold py-2 rounded-full transition-colors ${c.locked ? "bg-[#FF9F2E]/15 text-[#FF9F2E] hover:bg-[#FF9F2E]/25" : "border border-white/12 text-[#94A3B8] hover:border-[#FF9F2E] hover:text-[#FF9F2E]"}`}>
                    {c.locked ? <><Unlock className="w-4 h-4" /> Desbloquear</> : <><Lock className="w-4 h-4" /> Bloquear ADN</>}
                  </button>
                  <button data-testid={`character-edit-${c.id}`} onClick={() => openEdit(c)}
                    className="inline-flex items-center justify-center w-10 h-9 rounded-full border border-white/12 text-[#94A3B8] hover:text-white hover:border-white/25 transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button data-testid={`delete-character-${c.id}`} onClick={() => del(c.id)}
                    className="inline-flex items-center justify-center w-10 h-9 rounded-full border border-white/12 text-[#64748B] hover:text-red-400 hover:border-red-400/40 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        </>
      )}
    </div>
  );
}
